<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResizeObserver } from '@vueuse/core';
import { getInboxSource } from 'dashboard/helper/inbox';
import ChannelName from './ChannelName.vue';

// The channel name plus the address behind the inbox. A Phone inbox lists every
// one of its numbers here, which can run to a hundred or more, so the line is
// clamped to two lines and can be expanded by clicking it.
const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const textRef = useTemplateRef('textRef');
const isExpanded = ref(false);
const isClamped = ref(false);

const source = computed(() =>
  getInboxSource(
    props.inbox.channel_type,
    props.inbox.phone_number,
    props.inbox
  )
);

const measure = () => {
  // Nothing overflows while expanded, so keep the last collapsed measurement —
  // re-measuring here would hide the toggle the moment it is used.
  if (isExpanded.value) return;

  const el = textRef.value;
  isClamped.value = el ? el.scrollHeight - el.clientHeight > 1 : false;
};

// Fires on mount too, which gives us the initial measurement.
useResizeObserver(textRef, measure);
// The box can stay the same size while the text inside it changes.
watch(source, () => nextTick(measure));

const toggle = () => {
  if (!isClamped.value) return;
  isExpanded.value = !isExpanded.value;
};

// Clicking the line expands it, but dragging across it to copy a number should
// not collapse it again. Keyboard and assistive tech use the button below.
const onTextClick = () => {
  if (window.getSelection()?.toString()) return;

  toggle();
};

const toggleLabel = computed(() =>
  isExpanded.value
    ? t('COMPONENTS.SHOW_MORE_BLOCK.SHOW_LESS')
    : t('COMPONENTS.SHOW_MORE_BLOCK.SHOW_MORE')
);
</script>

<template>
  <div class="text-n-slate-11">
    <span
      ref="textRef"
      class="break-words"
      :class="{ 'line-clamp-2': !isExpanded, 'cursor-pointer': isClamped }"
      @click="onTextClick"
    >
      <ChannelName :channel-type="inbox.channel_type" />
      <template v-if="source">
        <span aria-hidden="true"> · </span>
        <span>{{ source }}</span>
      </template>
    </span>
    <button
      v-if="isClamped"
      type="button"
      class="block !p-0 text-n-brand hover:underline"
      @click="toggle"
    >
      {{ toggleLabel }}
    </button>
  </div>
</template>
